import { Inject, Injectable } from '@angular/core';
import {
  DevwareMetaData,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwOrmDataService,
  DwOrmDataServiceToken,
  DwQueryParam
} from '@devwareapps/devware-cap';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { ensureArray } from '../util/ensure-array.util';

interface LookupSizeInfo {
  [lookupIdOrMeaning: string]: boolean;
}

/**
 * Resolves lookup keys to their current CAP display values.
 *
 * Normal multi-select lookups are loaded as one cached collection. Large
 * lookups and single values use CAP's single-item endpoint so a display-only
 * operation cannot accidentally load an unbounded lookup.
 */
@Injectable({ providedIn: 'root' })
export class LookupDisplayResolverService {
  private lookupSizeInfo$: Observable<LookupSizeInfo>;

  constructor(
    @Inject(DwMetaDataServiceToken) private metaDataService: DwMetaDataService,
    @Inject(DwOrmDataServiceToken) private ormDataService: DwOrmDataService
  ) { }

  resolve(
    lookupId: number | string,
    value: any,
    queryParams: DwQueryParam[] = [],
    loadedOptions: any[] = []
  ): Observable<string> {
    const values = ensureArray(value);
    if (!values.length) {
      return of('');
    }

    const loadedDisplay = this.resolveFromItems(values, loadedOptions, false);
    if (loadedDisplay !== null) {
      return of(loadedDisplay);
    }

    if (values.length === 1) {
      return this.resolveIndividually(lookupId, values, queryParams);
    }

    return this.isLargeLookup(lookupId).pipe(
      switchMap(isLargeLookup => isLargeLookup
        ? this.resolveIndividually(lookupId, values, queryParams)
        : this.resolveFromFullLookup(lookupId, values, queryParams))
    );
  }

  private resolveFromFullLookup(
    lookupId: number | string,
    values: any[],
    queryParams: DwQueryParam[]
  ): Observable<string> {
    return this.metaDataService.getLookup(lookupId, queryParams).pipe(
      take(1),
      switchMap(items => {
        const display = this.resolveFromItems(values, items || [], true, lookupId);
        return display === null
          ? this.resolveIndividually(lookupId, values, queryParams)
          : of(display);
      }),
      catchError(error => {
        console.warn(`Lookup ${lookupId} could not be loaded for saved-filter display resolution.`, error);
        return this.resolveIndividually(lookupId, values, queryParams);
      })
    );
  }

  private resolveIndividually(
    lookupId: number | string,
    values: any[],
    queryParams: DwQueryParam[]
  ): Observable<string> {
    const useParametersOnSingleLookup = queryParams.length > 0;
    return forkJoin(values.map(itemValue =>
      this.metaDataService.getSingleLookupItem(
        lookupId,
        itemValue,
        useParametersOnSingleLookup,
        queryParams
      ).pipe(
        take(1),
        map(item => item?.Display || null),
        catchError(error => {
          console.warn(`Lookup ${lookupId} value ${itemValue} could not be resolved for saved-filter display.`, error);
          return of(null);
        })
      )
    )).pipe(
      map(displays => displays.map((display, index) => display || `${values[index]}`).join(', '))
    );
  }

  private resolveFromItems(
    values: any[],
    items: any[],
    warnForMissing: boolean,
    lookupId?: number | string
  ): string | null {
    if (!items?.length) {
      return null;
    }

    const itemsById = new Map<string, any>();
    items.forEach(item => itemsById.set(this.normalize(item?.Id), item));
    const resolvedItems = values.map(value => itemsById.get(this.normalize(value)));
    const missingValues = values.filter((_value, index) => !resolvedItems[index]);

    if (missingValues.length) {
      if (warnForMissing) {
        console.warn(`Lookup ${lookupId} did not return saved filter value(s): ${missingValues.join(', ')}.`);
      }
      return null;
    }

    return resolvedItems.map((item, index) => item?.Display || `${values[index]}`).join(', ');
  }

  private isLargeLookup(lookupId: number | string): Observable<boolean> {
    return this.getLookupSizeInfo().pipe(
      map(info => {
        const key = this.normalize(lookupId);
        if (Object.prototype.hasOwnProperty.call(info, key)) {
          return info[key];
        }

        // Unknown lookup definitions take the safe path and avoid a full load.
        console.warn(`Lookup size configuration was not found for ${lookupId}; using single-item display resolution.`);
        return true;
      })
    );
  }

  private getLookupSizeInfo(): Observable<LookupSizeInfo> {
    if (!this.lookupSizeInfo$) {
      const query = DevwareMetaData.DwMetaDataLookup.CreateQuery();
      query.IncludeSharedData = true;
      query.getFieldSettings().IncludeRootItemFields = false;
      query.getFieldSettings().LoadAllLookupDisplayFields = false;
      query.AddField(DevwareMetaData.DwMetaDataLookup.MetaDataLookupId);
      query.AddField(DevwareMetaData.DwMetaDataLookup.LookupMeaning);
      query.AddField(DevwareMetaData.DwMetaDataLookup.IsLargeLookup);

      this.lookupSizeInfo$ = this.ormDataService.executeQuery<any>(query).pipe(
        map(rows => (rows || []).reduce((result, row) => {
          const isLargeLookup = this.toBoolean(row.IsLargeLookup);
          result[this.normalize(row.MetaDataLookupId)] = isLargeLookup;
          if (row.LookupMeaning) {
            result[this.normalize(row.LookupMeaning)] = isLargeLookup;
          }
          return result;
        }, {} as LookupSizeInfo)),
        catchError(error => {
          console.warn('Lookup size metadata could not be loaded; using single-item display resolution.', error);
          return of({} as LookupSizeInfo);
        }),
        shareReplay(1)
      );
    }

    return this.lookupSizeInfo$;
  }

  private normalize(value: any): string {
    return value === null || value === undefined ? '' : `${value}`;
  }

  private toBoolean(value: any): boolean {
    return value === true || value === 1 || `${value}`.toLowerCase() === 'true' || `${value}` === '1';
  }
}
